import React, { Children, cloneElement } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useReducedMotion } from "framer-motion";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";

export default function MobileFoodCarousel({ children, sx, slidesToShow = 1, cardWidth = 280, centerPadding = "10%", className, breakpoint = "sm", variableWidth = false, gap = 16, responsiveCards = false }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(breakpoint));
  const reduceMotion = useReducedMotion();
  const slideGap = gap;
  const fittedSlides = slidesToShow;
  const slides = Children.toArray(children);
  const visibleSlides = Math.min(fittedSlides, slides.length);
  const canSlide = slides.length > visibleSlides;

  // CSS determines the layout before first paint; no measurement or autoplay shifts.
  if (responsiveCards) {
    return <Box className={className} sx={{
      ...sx, width: "100%", minWidth: 0,
      display: "flex", flexWrap: { xs: "nowrap", md: "wrap" },
      justifyContent: { xs: "flex-start", md: "center" },
      overflowX: { xs: "auto", md: "visible" },
      scrollSnapType: "x proximity", scrollbarWidth: "thin",
      "& > *": { flexShrink: 0, scrollSnapAlign: "start" },
    }}>{children}</Box>;
  }

  if (!isMobile || slides.length === 0) {
    return <Box sx={sx}>{children}</Box>;
  }

  return (
    <Box className={className} sx={{ width: "100%", minWidth: 0, maxWidth: "100%", "& .slick-list": { py: 1 }, "& .slick-track": { display: "flex", alignItems: "stretch", ...(variableWidth && !canSlide ? { width: "100% !important", justifyContent: "center" } : {}) } }}>
      <Box sx={{ width: "100%", maxWidth: responsiveCards ? visibleSlides * (cardWidth + slideGap) : "100%", mx: "auto" }}>
        <Slider
          arrows={false}
          dots={false}
          slidesToShow={visibleSlides}
          slidesToScroll={1}
          variableWidth={variableWidth}
          centerMode={canSlide && visibleSlides === 1}
          centerPadding={responsiveCards ? "0px" : centerPadding}
          infinite={canSlide}
          autoplay={!reduceMotion && canSlide}
          autoplaySpeed={2500}
          speed={reduceMotion ? 0 : 600}
          swipeToSlide
          pauseOnHover
          pauseOnFocus
        >
          {slides.map((slide) => (
            <div key={slide.key} style={variableWidth ? { width: cardWidth + slideGap } : undefined}>
              <Box sx={{ px: responsiveCards || variableWidth ? `${slideGap / 2}px` : 0.75, minWidth: 0 }}>
                {cloneElement(slide, { sx: { ...slide.props.sx, width: "100%", minWidth: 0, maxWidth: cardWidth, mx: "auto" } })}
              </Box>
            </div>
          ))}
        </Slider>
      </Box>
    </Box>
  );
}
